import dayjs from 'dayjs';
import { Box, Card, CardBody, CardFooter, CardHeader, Pagination, Text } from 'grommet';
import { Share } from 'grommet-icons';
import { kebabCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotificationStore, useProjectStore } from 'src/context';
import { borderRadiusSize } from 'src/theme';
import { NotificationIcon } from './NotificationIcon';

export const NotificationsTable = observer(() => {
  /** Context **/
  const notificationStore = useNotificationStore();
  const projectStore = useProjectStore();

  /** State **/
  const [isLoading, setIsLoading] = useState(true);

  /** Refs **/
  const columnWidths = [
    '3%', // type icon
    '16%', // timestamp
    '81%', // details
  ];

  /** Effects **/

  useEffect(() => {
    if (isLoading) notificationStore.list().finally(() => setIsLoading(false));
  }, [isLoading]);

  useEffect(() => {
    if (notificationStore.page) setIsLoading(true);
  }, [notificationStore.page]);

  useEffect(() => {
    return () => {
      notificationStore.markAsRead();
    };
  }, []);

  /** Render */
  return (
    <Card round={borderRadiusSize} background="light-1" animation={{ type: 'fadeIn', duration: 500 }}>
      {/* Header */}
      <CardHeader
        justify="start"
        align="center"
        gap="small"
        background={{ color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)', zIndex: 1 }}
        height={{ max: '80px', min: '80px' }}
        pad={{ horizontal: 'small' }}
      >
        <Text color="white">Notifications</Text>
      </CardHeader>

      {/* Body */}
      <CardBody background="white" elevation="xsmall">
        {/* Table Header */}
        <Box
          direction="row"
          justify="between"
          align="center"
          gap="medium"
          pad={{ vertical: 'xsmall', horizontal: 'small' }}
          background="light-2"
          animation={{ type: 'slideDown', size: 'medium', delay: 300 }}
          border={{ side: 'bottom', size: '1px', color: 'light-4' }}
        >
          <Box width={columnWidths[0]} align="center">
            <Text size="xsmall">TYPE</Text>
          </Box>
          <Box width={columnWidths[1]}>
            <Text size="xsmall">TIMESTAMP</Text>
          </Box>
          <Box width={columnWidths[2]}>
            <Text size="xsmall">DETAILS</Text>
          </Box>
        </Box>

        {notificationStore.notifications.length > 0 ? (
          notificationStore.notifications.map((n, i) => (
            <Box
              key={n.id}
              border={
                i < notificationStore.notifications.length - 1
                  ? { side: 'bottom', size: '1px', color: 'light-3' }
                  : undefined
              }
              direction="row"
              pad={{ horizontal: 'small', vertical: '1.1rem' }}
              gap="medium"
              align="center"
              justify="center"
              fill="horizontal"
            >
              <Box width={columnWidths[0]} align="center">
                <NotificationIcon notification={n} />
              </Box>
              <Box width={columnWidths[1]}>
                <Text>{dayjs(n.created_at).format('MMM D, YYYY @ h:mm:ss A')}</Text>
              </Box>
              <Box width={columnWidths[2]}>
                {projectStore.currentProject && n.source_entity_name && n.source_entity_id ? (
                  <Link
                    to={`/project/${kebabCase(projectStore.currentProject.name)}/${n.source_entity_name.toLocaleLowerCase()}/${n.source_entity_id}`}
                  >
                    <Box direction="row" gap="xsmall" align="center">
                      <Text truncate>{n.text}</Text>
                      <Share size="small" />
                    </Box>
                  </Link>
                ) : (
                  <Text truncate>{n.text}</Text>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Box pad="medium" align="center">
            <Text color="dark-3">No notifications found.</Text>
          </Box>
        )}
      </CardBody>

      {/* Footer */}
      <CardFooter justify="center" gap="small" background="light-2" pad="small">
        <Pagination
          page={notificationStore.page}
          step={notificationStore.limit}
          numberItems={notificationStore.total}
          onChange={(props) => notificationStore.setPage(props.page)}
        />
      </CardFooter>
    </Card>
  );
});
